import React, { useMemo } from 'react';
import cx from 'classnames';

import { Icon, Input, TextArea } from 'design-system-web';

import CheckboxGroup from 'components/form/CheckboxGroup';
import Dropdown from 'components/form/Dropdown';
import RadioGroup from 'components/form/RadioGroup';
import Label from 'components/general/Label';
import { VisitorCustomFieldType } from 'graphql-query-contracts';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';


import type { CustomFieldOptionItem, CustomFieldRendererProps } from './customField.types';

import styles from './CustomFieldRenderer.scss';

function getOptionDisplay(option: CustomFieldOptionItem): string {
  return typeof option === 'string' ? option : option.displayText;
}

function getOptionValue(option: CustomFieldOptionItem): string {
  return typeof option === 'string' ? option : option.value;
}

const OPTION_SCHEMA = { id: 'value' as const, text: 'displayText' as const };
const EMPTY_CUSTOM_FIELD_OPTIONS: CustomFieldOptionItem[] = [];

export function CustomFieldRenderer({
  field,
  disabled = true,
  value,
  onChange,
  error,
  isRequired,
}: CustomFieldRendererProps) {
  const optionList = field.options ?? EMPTY_CUSTOM_FIELD_OPTIONS;
  const isInteractive = !disabled && !!onChange;
  const stringValue = typeof value === 'string' ? value : '';
  const arrayValue = Array.isArray(value) ? value : [];

  const items = useMemo(
    () =>
      optionList.map(opt => ({
        value: getOptionValue(opt),
        displayText: getOptionDisplay(opt),
      })),
    [optionList]
  );

  function renderSelectField() {
    const dropdownItems = items.map(opt => ({ value: opt.value, label: opt.displayText }));

    return (
      <div data-test={`customField-select-${field.fieldKey}`}>
        <Dropdown
          disabled={disabled}
          placeholder={field.placeholder ?? ''}
          value={stringValue !== '' ? stringValue : null}
          items={dropdownItems}
          onValueChange={isInteractive ? val => onChange(val) : () => undefined}
          doTranslation={false}
        />
      </div>
    );
  }

  function renderRadioField() {
    return (
      <div className={styles.options} data-test={`customField-radio-${field.fieldKey}`}>
        <RadioGroup
          name={field.fieldKey}
          disabled={disabled}
          selected={isInteractive ? stringValue || null : null}
          schema={OPTION_SCHEMA}
          items={items}
          onChange={isInteractive ? selected => onChange(selected) : () => undefined}
          doTranslate={false}
        />
      </div>
    );
  }

  function renderCheckboxField() {
    return (
      <div className={styles.options} data-test={`customField-checkbox-${field.fieldKey}`}>
        <CheckboxGroup
          name={field.fieldKey}
          disabled={disabled}
          selected={isInteractive ? arrayValue : []}
          schema={OPTION_SCHEMA}
          items={items}
          onChange={
            isInteractive ? (values: string[]) => onChange(values) : () => undefined
          }
        />
      </div>
    );
  }

  function renderTextareaField() {
    return (
      <TextArea
        value={stringValue}
        onChange={isInteractive ? val => onChange(val) : () => undefined}
        placeholder={field.placeholder ?? ''}
        disabled={disabled}
        testId={`customField-textarea-${field.fieldKey}`}
      />
    );
  }

  function renderTextField() {
    return (
      <Input
        fieldName={field.fieldKey}
        value={stringValue}
        onChange={isInteractive ? val => onChange(val) : () => undefined}
        placeholder={field.placeholder ?? ''}
        disabled={disabled}
        testId={`customField-text-${field.fieldKey}`}
      />
    );
  }

  function renderInput() {
    switch (field.fieldType) {
      case VisitorCustomFieldType.Textarea:
        return renderTextareaField();
      case VisitorCustomFieldType.SelectDd:
        return renderSelectField();
      case VisitorCustomFieldType.SelectRadio:
        return renderRadioField();
      case VisitorCustomFieldType.MultiSelect:
        return renderCheckboxField();
      default:
        return renderTextField();
    }
  }

  function renderInputWithIcon() {
    const input = renderInput();

    // Radio and checkbox are multi-element fieldsets — no icon overlay
    const supportsIconOverlay =
      field.fieldType !== VisitorCustomFieldType.SelectRadio &&
      field.fieldType !== VisitorCustomFieldType.MultiSelect;

    if (!error || !supportsIconOverlay) {
      return input;
    }

    const isSelect = field.fieldType === VisitorCustomFieldType.SelectDd;

    return (
      <div className={cx(styles.inputWithIconWrapper, styles.hasErrorIcon)}>
        {input}
        <div className={cx(styles.errorIconOverlay, { [styles.errorIconOverlaySelect]: isSelect })}>
          <div className={cx(styles.errorIconOverlayInner, { [styles.errorIconOverlayInnerSelect]: isSelect })}>
            <Icon
              name="exclamation-circle"
              set={ICON_SET_FONTAWESOME}
              size="small"
              className={styles.errorIconSvg}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cx(styles.fieldWrapper, {
        [styles.interactive]: isInteractive,
        [styles.hasError]: !!error,
      })}
      data-test={`customFieldRenderer-${field.fieldKey}`}
    >

      <Label>
        {field.fieldName}
        {isRequired && <span className={styles.required}>*</span>}
      </Label>
      {renderInputWithIcon()}
      {error && (
        <p
          className={styles.fieldError}
          data-test={`customFieldError-${field.fieldKey}`}
          role="alert"
        >
          {error}
        </p>
      )}

    </div>
  );
}

